import { formatDate } from 'lib/datetime';
import { getFeaturedImageUrl } from 'lib/util';

export const SITE_URL = process.env.NEXT_PUBLIC_SITE_URL || 'https://turnstyleonline.com';

// Helper function to get absolute URL
export const getAbsoluteUrl = (path) => {
  if (!path || path === 'undefined' || path === 'null' || path.includes('undefined') || path.includes('/undefined')) {
    console.warn('[newsletter] getAbsoluteUrl called with invalid path:', path);
    return SITE_URL; // Return homepage if path is invalid
  }
  if (path.startsWith('http')) return path;
  const cleanPath = path.startsWith('/') ? path : `/${path}`;
  return `${SITE_URL}${cleanPath}`;
};

// Helper to append UTM params so newsletter clicks are attributable in GA
export const withUtm = (url, campaign, content) => {
  if (!url) return url;
  const params = new URLSearchParams({
    utm_source: 'newsletter',
    utm_medium: 'email',
    utm_campaign: campaign,
    utm_content: content,
  });
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}${params.toString()}`;
};

// Helper function to check if event/show is upcoming (matches EventsList logic)
export const isUpcoming = (item, dateField = 'eventDate') => {
  if (!item?.[dateField]) return false;

  // Compare calendar days as YYYY-MM-DD strings in Richmond's timezone. Parsing a
  // date-only string with new Date() yields UTC midnight (the previous evening in
  // ET), which dropped events happening today.
  const todayET = new Intl.DateTimeFormat('en-CA', { timeZone: 'America/New_York' }).format(new Date());
  return String(item[dateField]).slice(0, 10) >= todayET;
};

// Helper to read a post/show/event's tags regardless of whether they've been flattened (posts) or are still in raw {edges} shape (shows/events)
export const getItemTags = (item) => {
  if (Array.isArray(item?.tags)) return item.tags;
  if (Array.isArray(item?.tags?.edges)) return item.tags.edges.map((edge) => edge.node);
  return [];
};

// Helper to escape HTML entities
export const escapeHtml = (text) => {
  if (!text) return '';
  const map = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  };
  return String(text).replace(/[&<>"']/g, (m) => map[m]);
};

// Helper to sanitize HTML for email and strip paragraph tags
export const sanitizeHtml = (html) => {
  if (!html) return '';
  // Remove script tags and other unsafe content
  let cleaned = html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/on\w+="[^"]*"/gi, '')
    .replace(/on\w+='[^']*'/gi, '');

  // Strip <p> tags but keep the content
  cleaned = cleaned
    .replace(/<p[^>]*>/gi, '')
    .replace(/<\/p>/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return cleaned;
};

// Email clients don't support CSS variables or the site's fonts, so the palette is
// inlined as literals that mirror the site tokens (brand, brand-dark, brand-contrast, accent-pink).
export const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";
export const C = {
  canvas: '#0d0816',
  night: '#1a0a2e',
  brand: '#ba75ff',
  brandDark: '#5B1FA8',
  pink: '#FF1B87',
  ink: '#111827',
  body: '#374151',
  muted: '#6b7280',
  line: '#ece6f5',
  tint: '#f7f3fd',
  youtube: '#CC0000',
};

export const YOUTUBE_CHANNEL_URL = 'https://www.youtube.com/@TurnstyleRVA';
// Email clients need absolute URLs; dev serves the asset locally so the preview works before deploy.
export const HEADER_BG_IMAGE = `${process.env.NODE_ENV === 'development' ? '' : SITE_URL}/newsletter/header-globe.png`;
export const LOGO_IMAGE = 'https://turnstyleonline.com/wp-content/uploads/sites/6/2023/01/cropped-ts-icon-2023-1.png';

export const chamfer = (cut) =>
  `-webkit-clip-path: polygon(0 0, calc(100% - ${cut}px) 0, 100% ${cut}px, 100% 100%, ${cut}px 100%, 0 calc(100% - ${cut}px)); clip-path: polygon(0 0, calc(100% - ${cut}px) 0, 100% ${cut}px, 100% 100%, ${cut}px 100%, 0 calc(100% - ${cut}px));`;

export const BUTTONS = {
  solid: `background-color: ${C.night}; color: #ffffff; border: 2px solid ${C.night};`,
  tickets: `background-color: ${C.brandDark}; color: #ffffff; border: 2px solid ${C.brandDark};`,
  outline: `background-color: transparent; color: ${C.night}; border: 2px solid ${C.night};`,
  light: `background-color: #ffffff; color: ${C.night}; border: 2px solid #ffffff;`,
  youtube: `background-color: ${C.youtube}; color: #ffffff; border: 2px solid ${C.youtube};`,
};

export const button = (href, label, variant = 'solid', size = 'md') => {
  const pad = size === 'lg' ? '14px 26px' : size === 'sm' ? '8px 14px' : '11px 20px';
  const fontSize = size === 'lg' ? 16 : size === 'sm' ? 13 : 14;
  return `<a href="${href}" style="display: inline-block; padding: ${pad}; ${
    BUTTONS[variant]
  } text-decoration: none; font-family: ${FONT}; font-size: ${fontSize}px; font-weight: 800; letter-spacing: 0.3px; line-height: 1.2; white-space: nowrap; ${chamfer(
    8
  )}">${label}</a>`;
};

export const buttonRow = (buttons) =>
  `<table role="presentation" cellspacing="0" cellpadding="0" border="0"><tr>${buttons
    .filter(Boolean)
    .map(
      (b, i) => `${i ? '<td width="8" style="font-size: 0;">&nbsp;</td>' : ''}<td style="padding-top: 6px;">${b}</td>`
    )
    .join('')}</tr></table>`;

export const sectionHeader = (kicker, title, { dark = false } = {}) => `
              <p style="margin: 0 0 6px 0; font-family: ${FONT}; font-size: 12px; font-weight: 800; letter-spacing: 2px; text-transform: uppercase; color: ${
  dark ? C.brand : C.pink
};">${kicker}</p>
              <h2 style="text-transform: none; margin: 0 0 6px 0; padding: 0; font-family: ${FONT}; font-size: 26px; font-weight: 900; letter-spacing: -0.5px; line-height: 1.15; color: ${
  dark ? '#ffffff' : C.ink
};">${title}</h2>
              <table role="presentation" cellspacing="0" cellpadding="0" border="0" style="margin: 0 0 24px 0;"><tr>
                <td width="48" height="4" style="background-color: ${
                  C.brand
                }; font-size: 0; line-height: 0;">&nbsp;</td>
                <td width="16" height="4" style="background-color: ${C.pink}; font-size: 0; line-height: 0;">&nbsp;</td>
              </tr></table>`;

export const truncate = (html, max = 150) => {
  const text = sanitizeHtml(html);
  return text.length > max ? `${text.substring(0, max).trim()}…` : text;
};

// WordPress ticket URLs sometimes carry stray whitespace or omit the scheme.
export const getTicketUrl = (event) => {
  const url = (event?.eventTickets || '').trim();
  if (!url) return '';
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

export const getEventLocation = (event) => {
  const city = (event?.eventCity || event?.venue?.venueCity || '').trim();
  const state = (event?.venue?.venueState || '').trim();
  const place = [city, state].filter(Boolean).join(', ');
  return [event?.eventVenue, place].filter(Boolean).join(' · ');
};

export const dateBadge = (dateString) => `
                <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="64" style="background-color: ${
                  C.night
                }; ${chamfer(8)}">
                  <tr><td align="center" style="padding: 8px 0 0 0; font-family: ${FONT}; font-size: 11px; font-weight: 800; letter-spacing: 1.5px; text-transform: uppercase; color: ${
  C.brand
};">${formatDate(dateString, 'MMM')}</td></tr>
                  <tr><td align="center" style="padding: 0; font-family: ${FONT}; font-size: 28px; font-weight: 900; line-height: 1.1; color: #ffffff;">${formatDate(
  dateString,
  'd'
)}</td></tr>
                  <tr><td align="center" style="padding: 0 0 8px 0; font-family: ${FONT}; font-size: 10px; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: #d8c4f5;">${formatDate(
  dateString,
  'EEE'
)}</td></tr>
                </table>`;

export const thumb = (src, alt, width, height, cut = 10) =>
  `<img src="${src}" alt="${escapeHtml(
    alt
  )}" width="${width}" height="${height}" style="width: ${width}px; height: ${height}px; max-width: 100%; object-fit: cover; display: block; border: 0; ${chamfer(
    cut
  )}" />`;

// Upcoming-event row: thumbnail, date badge, title/location, excerpt, tickets + details.
export const eventCard = ({ event, trackedUrl }) => {
  const ticketUrl = getTicketUrl(event);
  const location = getEventLocation(event);
  const detailsUrl = trackedUrl(`/event/${event.slug}`, `upcoming_event_learn_more_${event.slug}`);
  return `
            <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="margin-bottom: 16px; background-color: #ffffff; border: 1px solid ${
              C.line
            };">
              <tr>
                <td class="nl-stack nl-thumb" width="156" valign="top" style="padding: 16px 0 16px 16px;">
                  <a href="${detailsUrl}">${thumb(
    getFeaturedImageUrl(event.featuredImage),
    event.title || 'Event image',
    140,
    140
  )}</a>
                </td>
                <td class="nl-stack" valign="top" style="padding: 16px 18px;">
                  <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
                    <tr>
                      ${
                        event.eventDate
                          ? `<td width="76" valign="top" style="padding-right: 12px;">${dateBadge(
                              event.eventDate
                            )}</td>`
                          : ''
                      }
                      <td valign="top">
                        <h3 style="text-transform: none; margin: 0 0 4px 0; padding: 0; font-family: ${FONT}; font-size: 18px; font-weight: 800; line-height: 1.25; color: ${
    C.ink
  };">
                          <a href="${trackedUrl(
                            `/event/${event.slug}`,
                            `upcoming_event_title_${event.slug}`
                          )}" style="color: ${C.ink}; text-decoration: none;">${escapeHtml(
    event.title || 'Untitled Event'
  )}</a>
                        </h3>
                        ${
                          location
                            ? `<p style="margin: 0; font-family: ${FONT}; font-size: 13px; font-weight: 600; color: ${
                                C.muted
                              };">${escapeHtml(location)}</p>`
                            : ''
                        }
                      </td>
                    </tr>
                  </table>
                  ${
                    event.excerpt
                      ? `<p style="margin: 12px 0 0 0; font-family: ${FONT}; font-size: 14px; line-height: 1.55; color: ${
                          C.body
                        };">${truncate(event.excerpt)}</p>`
                      : ''
                  }
                  <div style="padding-top: 12px;">
                    ${buttonRow([
                      ticketUrl &&
                        button(
                          trackedUrl(ticketUrl, `upcoming_event_tickets_${event.slug}`),
                          'Get Tickets &rarr;',
                          'tickets',
                          'sm'
                        ),
                      button(detailsUrl, 'Details', 'outline', 'sm'),
                    ])}
                  </div>
                </td>
              </tr>
            </table>`;
};

// Hero header shared by every email: logo + date, kicker/title/subtitle, section nav.
export const emailHeader = ({ trackedUrl, dateLabel, title, subtitle, kicker = 'Turnstyle &middot; Richmond, VA' }) => `
          <!-- Hero Header (background image; solid night fallback for clients that drop it) -->
          <tr>
            <td background="${HEADER_BG_IMAGE}" bgcolor="${C.night}" valign="top" style="background-color: ${
  C.night
}; background-image: url('${HEADER_BG_IMAGE}'); background-size: auto 100%; background-position: right center; background-repeat: no-repeat;">
              <!-- Outlook desktop drops the background image and shows the solid night fill. -->
              <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" style="background-image: linear-gradient(90deg, rgba(26, 10, 46, 0.9) 0%, rgba(26, 10, 46, 0.55) 55%, rgba(91, 31, 168, 0.15) 100%);">
                <tr>
                  <td class="nl-pad" style="padding: 28px 40px 0 40px;">
                    <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%">
                      <tr>
                        <td valign="middle">
                          <a href="${trackedUrl(
                            '/',
                            'header_logo'
                          )}"><img src="${LOGO_IMAGE}" alt="Turnstyle" width="48" height="48" style="display: block; width: 48px; height: 48px; border: 0;" /></a>
                        </td>
                        <td align="right" valign="middle" style="font-family: ${FONT}; font-size: 12px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase; color: #d8c4f5;">
                          ${dateLabel}
                        </td>
                      </tr>
                    </table>
                  </td>
                </tr>
                <tr>
                  <td class="nl-pad" style="padding: 56px 40px 12px 40px;">
                    <p style="margin: 0 0 10px 0; font-family: ${FONT}; font-size: 13px; font-weight: 800; letter-spacing: 3px; text-transform: uppercase; color: ${
  C.brand
};">${kicker}</p>
                    <h1 class="nl-hero-title" style="text-transform: none; margin: 0; padding: 0; font-family: ${FONT}; font-size: 44px; font-weight: 900; line-height: 1.05; letter-spacing: -1px; color: #ffffff;">
                      ${title}
                    </h1>
                    <table role="presentation" cellspacing="0" cellpadding="0" border="0" style="margin: 18px 0 0 0;"><tr>
                      <td width="64" height="4" style="background-color: ${
                        C.brand
                      }; font-size: 0; line-height: 0;">&nbsp;</td>
                      <td width="20" height="4" style="background-color: ${
                        C.pink
                      }; font-size: 0; line-height: 0;">&nbsp;</td>
                    </tr></table>
                    <p style="margin: 16px 0 0 0; font-family: ${FONT}; font-size: 16px; line-height: 1.5; color: #e9ddfa; max-width: 440px;">
                      ${subtitle}
                    </p>
                  </td>
                </tr>
                <tr>
                  <td class="nl-pad" style="padding: 24px 40px 32px 40px;">
                    <p style="margin: 0; font-family: ${FONT}; font-size: 13px; line-height: 2;">
                      ${[
                        ['Events', '/events', 'nav_events'],
                        ['Shows', '/shows', 'nav_shows'],
                        ['Features', '/posts', 'nav_posts'],
                        ['YouTube', YOUTUBE_CHANNEL_URL, 'nav_youtube'],
                      ]
                        .map(
                          ([label, path, content]) =>
                            `<a href="${trackedUrl(
                              path,
                              content
                            )}" style="font-family: ${FONT}; font-size: 13px; font-weight: 800; letter-spacing: 1px; text-transform: uppercase; color: #ffffff; text-decoration: none; white-space: nowrap;">${label}</a>`
                        )
                        .join(
                          // Inline links, not table cells: mobile clients squeezed the cells and wrapped the last letter.
                          ` <span style="padding: 0 8px; color: ${C.brand};">/</span> `
                        )}
                    </p>
                  </td>
                </tr>
              </table>
            </td>
          </tr>`;

// Podcast platforms, disclaimer and logo footer shared by every email.
export const emailFooter = ({ trackedUrl }) => `
          <!-- Podcast Platforms / Footer -->
          <tr>
            <td class="nl-pad" bgcolor="${C.night}" style="padding: 40px 40px 36px 40px; background-color: ${
  C.night
}; text-align: center;">
              <p style="margin: 0 0 6px 0; font-family: ${FONT}; font-size: 12px; font-weight: 800; letter-spacing: 2px; text-transform: uppercase; color: ${
  C.brand
};">Frequency &middot; Turnstyle Radio Show</p>
              <h3 style="text-transform: none; margin: 0 0 20px 0; padding: 0; font-family: ${FONT}; font-size: 20px; font-weight: 900; color: #ffffff;">
                Listen on Your Favorite Platform
              </h3>
              <p style="margin: 0; font-size: 0; line-height: 0;">
                ${[
                  [
                    'Apple Podcasts',
                    'https://podcasts.apple.com/us/podcast/frequency---turnstyle-radio-show/id1830590852',
                    'apple_podcasts',
                  ],
                  [
                    'Amazon Music',
                    'https://music.amazon.com/podcasts/f6690de2-28a5-410f-a961-fd12355317ea/frequency---turnstyle-radio-show',
                    'amazon_music',
                  ],
                  [
                    'TuneIn',
                    'https://tunein.com/podcasts/Music-Podcasts/FREQUENCY---Turnstyle-Radio-Show-p4662781/?topicId=515726328',
                    'tunein',
                  ],
                  ['WRIR 97.3FM', 'https://www.wrir.org/show/frequency/', 'wrir'],
                  ['YouTube', YOUTUBE_CHANNEL_URL, 'youtube_platform_button'],
                ]
                  .map(
                    ([label, url, content]) =>
                      `<span style="display: inline-block; margin: 0 4px 8px 4px;">${button(
                        trackedUrl(url, content),
                        label,
                        'light',
                        'sm'
                      )}</span>`
                  )
                  .join('')}
              </p>
              <p style="margin: 20px auto 0 auto; max-width: 460px; font-family: ${FONT}; font-size: 12px; line-height: 1.6; color: #b9a6d6;">
                Turnstyle isn&rsquo;t monetized. We don&rsquo;t make any money from the ads shown on YouTube, podcast apps, or any other platform. For ad-free listening, stream the full archives on <a href="${trackedUrl(
                  '/shows',
                  'disclaimer_archives_turnstyle'
                )}" style="color: #e9ddfa; text-decoration: underline;">turnstyleonline.com</a> or <a href="${trackedUrl(
  'https://www.wrir.org/show/frequency/',
  'disclaimer_archives_wrir'
)}" style="color: #e9ddfa; text-decoration: underline;">wrir.org</a>. We do this for the love of the music.
              </p>
              <a href="${trackedUrl(
                '/',
                'footer_logo'
              )}"><img src="${LOGO_IMAGE}" alt="Turnstyle" width="40" height="40" style="display: block; width: 40px; height: 40px; border: 0; margin: 28px auto 10px auto;" /></a>
              <p style="margin: 0; font-family: ${FONT}; font-size: 12px; line-height: 1.6; color: #b9a6d6;">
                <a href="${trackedUrl(
                  '/',
                  'footer_site'
                )}" style="color: #b9a6d6; text-decoration: none;">turnstyleonline.com</a>
              </p>
            </td>
          </tr>`;

export const EMAIL_STYLES = `
        /* Reset global styles for newsletter */
        .newsletter-wrapper p, .newsletter-wrapper h1, .newsletter-wrapper h2, .newsletter-wrapper h3 { margin: 0; padding: 0; }
        .newsletter-wrapper * { box-sizing: border-box; }
        .newsletter-wrapper a:hover { opacity: 0.88; }
        @media only screen and (max-width: 600px) {
          .nl-pad { padding-left: 20px !important; padding-right: 20px !important; }
          .nl-stack { display: block !important; width: 100% !important; }
          .nl-thumb { padding: 16px 16px 0 16px !important; }
          .nl-thumb img { width: 100% !important; height: auto !important; aspect-ratio: 16 / 10; }
          .nl-hero-title { font-size: 34px !important; }
          .nl-hide-sm { display: none !important; }
        }
      `;

// Preheader + 650px content table around the section rows.
export const emailShell = ({ preheader, rows }) => `
  <!-- Preheader (inbox preview text) -->
  <div style="display: none; max-height: 0; overflow: hidden; mso-hide: all; font-size: 1px; line-height: 1px; color: ${
    C.canvas
  }; opacity: 0;">
    ${escapeHtml(
      preheader
    )}&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;&nbsp;&zwnj;
  </div>
  <!-- Main Container Table -->
  <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="100%" bgcolor="${
    C.canvas
  }" style="background-color: ${C.canvas}; padding: 24px 0; font-family: ${FONT};">
    <tr>
      <td align="center" style="padding: 0 8px;">
        <!-- Content Table - 650px wide -->
        <table role="presentation" cellspacing="0" cellpadding="0" border="0" width="650" style="background-color: #ffffff; max-width: 650px; width: 100%; margin: 0 auto;">
${rows}
        </table>
      </td>
    </tr>
  </table>
        `;
